// ═══════════════════════════════════════════════════════════════
// BLACKBOARD — Classrooms (ported from Firebase Classrooms.jsx)
// Class scheduling, registration, attendance, certificates
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import { usePolling } from '../../hooks/usePolling';
import {
  Box,
  Paper,
  Text,
  Group,
  SimpleGrid,
  Button,
  Modal,
  TextInput,
  Select,
  NumberInput,
  Textarea,
  Badge,
  Progress,
  Tabs,
  Table,
  Checkbox,
  Stack,
  Loader,
  Center,
  Divider,
  ActionIcon,
} from '../../components/gab/GabComponents';
import { DateInput, TimeInput } from '../../components/gab/MantineDates';
import { notifications } from '../../lib/notifications';
import {
  IconPlus,
  IconCalendar,
  IconUser,
  IconMapPin,
  IconHistory,
  IconFileText,
  IconCheck,
  IconArrowBackUp,
  IconCertificate,
  IconX,
  IconBook,
  IconSettings,
  IconDownload,
  IconSchool,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBlackboardStore, type Classroom, type Training, type BBUser } from '../../store/blackboardStore';
import { useAuthStore } from '../../store/authStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import { generateCertCode, generateCertificatePDF } from './utils/certificateUtils';

// ── Helpers ──────────────────────────────────────────────────

interface RegisteredUser {
  userId: string;
  userName: string;
  email: string;
  registeredAt: string;
}

function parseJsonArray<T = unknown>(val: string | undefined | null): T[] {
  if (!val || val === '[]') return [];
  try { return JSON.parse(val) as T[]; } catch { return []; }
}

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  open: { color: 'green', label: 'Open' },
  full: { color: 'orange', label: 'Full' },
  completed: { color: 'blue', label: 'Completed' },
  draft: { color: 'gray', label: 'Draft' },
  cancelled: { color: 'red', label: 'Cancelled' },
};

function classStatusBadge(status: string) {
  const cfg = STATUS_CONFIG[status] || { color: 'gray', label: status };
  return <Badge color={cfg.color}>{cfg.label}</Badge>;
}

// Small inline component for downloading materials on My Classes cards
function MyClassMaterials({ classroomId }: { classroomId: number }) {
  const [mats, setMats] = useState<ClassMaterial[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    fetch(`${WORKER_BASE}/api/config/bb-classroom-materials-${classroomId}`, {
      headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
    }).then(r => r.ok ? r.json() : []).then(d => setMats(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [classroomId]);
  if (mats.length === 0) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <Button size="xs" variant="subtle" color="gray" fullWidth justify="flex-start"
        leftSection={<IconFileText size={12} />}
        onClick={() => setOpen(!open)}>
        {open ? 'Hide' : 'View'} Materials ({mats.length})
      </Button>
      {open && (
        <Stack gap={4} mt={4} pl="xs">
          {mats.map((mat, i) => (
            <Group key={i} gap="xs">
              <IconDownload size={10} color="#627d98" />
              <Text size="xs" c="blue" fw={500} style={{ cursor: 'pointer' }}
                onClick={() => {
                  const blob = new Blob([Uint8Array.from(atob(mat.data), c => c.charCodeAt(0))], { type: mat.type });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(blob);
                  a.download = mat.name;
                  a.click();
                  URL.revokeObjectURL(a.href);
                }}>
                {mat.name}
              </Text>
              <Text size="xs" c="dimmed">({(mat.size / 1024).toFixed(0)} KB)</Text>
            </Group>
          ))}
        </Stack>
      )}
    </div>
  );
}

const loadLogoAsBase64 = async (): Promise<string | null> => {
  try {
    const response = await fetch('/logo.jpg');
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// ── Class Card ───────────────────────────────────────────────

interface ClassCardProps {
  classroom: Classroom;
  currentUserId: string;
  userRole: string;
  isRegistered: boolean;
  onManage: (c: Classroom) => void;
  onEdit: (c: Classroom) => void;
  onRegister: (c: Classroom) => void;
  onUnregister: (c: Classroom) => void;
}

function ClassCard({ classroom, currentUserId, userRole, isRegistered, onManage, onEdit, onRegister, onUnregister }: ClassCardProps) {
  const canManage = userRole === 'admin' ||
    (userRole === 'trainer' && (classroom.creatorId === currentUserId || classroom.instructorId === currentUserId));

  const registered = parseJsonArray<RegisteredUser>(classroom.registeredUsers);
  const registeredCount = registered.length;
  const capacityPct = classroom.capacity > 0 ? (registeredCount / classroom.capacity) * 100 : 0;
  const isFull = registeredCount >= (classroom.capacity || 0);
  const displayStatus = isFull && classroom.status === 'open' ? 'full' : classroom.status;
  const classDate = classroom.classDate ? dayjs(classroom.classDate) : null;

  // Load materials count for registered students
  const [matCount, setMatCount] = useState(0);
  const [cardMaterials, setCardMaterials] = useState<ClassMaterial[]>([]);
  const [showMaterials, setShowMaterials] = useState(false);
  useEffect(() => {
    if (!isRegistered && !canManage) return;
    fetch(`${WORKER_BASE}/api/config/bb-classroom-materials-${classroom.id}`, {
      headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
    }).then(r => r.ok ? r.json() : []).then(d => {
      const arr = Array.isArray(d) ? d : [];
      setMatCount(arr.length);
      setCardMaterials(arr);
    }).catch(() => {});
  }, [classroom.id, isRegistered, canManage]);

  return (
    <Paper
      p="md"
      withBorder
      style={{
        cursor: 'default',
        transition: 'transform 150ms ease, box-shadow 150ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(17,45,78,.06), 0 12px 36px rgba(17,45,78,.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      <Group justify="space-between" align="flex-start" mb="xs">
        <Text fw={600} size="sm" style={{ flex: 1, paddingRight: 8 }}>
          {classroom.title}
        </Text>
        <Group gap={4}>
          {isRegistered && <Badge color="blue">Registered</Badge>}
          {classStatusBadge(displayStatus)}
        </Group>
      </Group>

      <Stack gap={4} mb="sm">
        <Group gap="xs">
          <IconCalendar size={12} color="#627d98" />
          <Text size="xs" c="dimmed">
            {classDate ? classDate.format('MMM D, YYYY') : '--'}
            {classroom.classTime ? ` | ${classroom.classTime}` : ''}
          </Text>
        </Group>
        <Group gap="xs">
          <IconUser size={12} color="#627d98" />
          <Text size="xs" c="dimmed">
            {classroom.instructorName || '--'}
          </Text>
        </Group>
        <Group gap="xs">
          <IconMapPin size={12} color="#627d98" />
          <Text size="xs" c="dimmed">
            {classroom.location || 'Virtual'}
          </Text>
        </Group>
      </Stack>

      <Text size="xs" c="dimmed" mb={4}>
        Capacity: {registeredCount}/{classroom.capacity || '--'}
      </Text>
      <Progress value={capacityPct} color={capacityPct >= 100 ? 'orange' : 'blue'} size="sm" mb="sm" />

      {/* Materials download for registered students */}
      {(isRegistered || canManage) && matCount > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Button size="xs" variant="subtle" color="gray" fullWidth justify="flex-start"
            leftSection={<IconFileText size={12} />}
            onClick={() => setShowMaterials(!showMaterials)}>
            {showMaterials ? 'Hide' : 'Show'} Materials ({matCount})
          </Button>
          {showMaterials && (
            <Stack gap={4} mt={4} pl="xs">
              {cardMaterials.map((mat, i) => (
                <Group key={i} gap="xs">
                  <IconDownload size={10} color="#627d98" />
                  <Text size="xs" c="blue" fw={500} style={{ cursor: 'pointer' }}
                    onClick={() => {
                      const blob = new Blob([Uint8Array.from(atob(mat.data), c => c.charCodeAt(0))], { type: mat.type });
                      const a = document.createElement('a');
                      a.href = URL.createObjectURL(blob);
                      a.download = mat.name;
                      a.click();
                      URL.revokeObjectURL(a.href);
                    }}>
                    {mat.name}
                  </Text>
                  <Text size="xs" c="dimmed">({(mat.size / 1024).toFixed(0)} KB)</Text>
                </Group>
              ))}
            </Stack>
          )}
        </div>
      )}

      <Group justify="flex-end" gap="xs">
        {canManage && (
          <>
            <Button size="xs" variant="light" color="gray" onClick={() => onEdit(classroom)}>
              Edit
            </Button>
            <Button size="xs" variant="light" color="blue" onClick={() => onManage(classroom)}>
              Manage
            </Button>
          </>
        )}
        {userRole === 'student' && classroom.status === 'open' && !isRegistered &&
          !(classDate && classDate.isBefore(dayjs(), 'day')) && (
          <Button size="xs" color="blue" onClick={() => onRegister(classroom)}>
            {isFull ? 'Join Waitlist' : 'Register'}
          </Button>
        )}
        {userRole === 'student' && isRegistered && classroom.status !== 'completed' && classroom.status !== 'cancelled' && (
          <Button size="xs" variant="outline" color="red" onClick={() => onUnregister(classroom)}>
            Unregister
          </Button>
        )}
      </Group>
    </Paper>
  );
}

// ── Create / Edit Modal ──────────────────────────────────────

interface CreateEditModalProps {
  opened: boolean;
  onClose: () => void;
  classroom: Classroom | null;
  trainings: Training[];
  instructors: BBUser[];
  onSaved: () => void;
}

function CreateEditModal({ opened, onClose, classroom, trainings, instructors, onSaved }: CreateEditModalProps) {
  const { createClassroom, updateClassroom } = useBlackboardStore();
  const user = useAuthStore((s) => s.user);
  const [saving, setSaving] = useState(false);

  const [formTrainingId, setFormTrainingId] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formDate, setFormDate] = useState<string | null>(null);
  const [formStartTime, setFormStartTime] = useState('');
  const [formEndTime, setFormEndTime] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formInstructorId, setFormInstructorId] = useState('');
  const [formCapacity, setFormCapacity] = useState<number | string>(20);
  const [formDescription, setFormDescription] = useState('');
  const [formStatus, setFormStatus] = useState('draft');

  useEffect(() => {
    if (opened) {
      if (classroom) {
        setFormTrainingId(String(classroom.trainingId || ''));
        setFormTitle(classroom.title || '');
        setFormDate(classroom.classDate || null);
        const timeParts = (classroom.classTime || '').split(' - ');
        setFormStartTime(timeParts[0] || '');
        setFormEndTime(timeParts[1] || '');
        setFormLocation(classroom.location || '');
        setFormInstructorId(classroom.instructorId || '');
        setFormCapacity(classroom.capacity || 20);
        setFormDescription('');
        setFormStatus(classroom.status || 'draft');
      } else {
        setFormTrainingId('');
        setFormTitle('');
        setFormDate(null);
        setFormStartTime('');
        setFormEndTime('');
        setFormLocation('');
        setFormInstructorId('');
        setFormCapacity(20);
        setFormDescription('');
        setFormStatus('draft');
      }
    }
  }, [opened, classroom]);

  const handleTrainingChange = (val: string | null) => {
    setFormTrainingId(val || '');
    if (val) {
      const t = trainings.find((tr) => String(tr.id) === val);
      if (t && !formTitle) setFormTitle(t.name);
    }
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      notifications.show({ title: 'Validation', message: 'Title is required.', color: 'yellow' });
      return;
    }
    if (!formDate) {
      notifications.show({ title: 'Validation', message: 'Date is required.', color: 'yellow' });
      return;
    }

    setSaving(true);
    try {
      const instructor = instructors.find((i) => String(i.id) === formInstructorId);
      const timeStr = [formStartTime, formEndTime].filter(Boolean).join(' - ');
      const instructorName = instructor
        ? `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim()
        : '';

      const data: Partial<Classroom> = {
        trainingId: formTrainingId || '',
        title: formTitle.trim(),
        classDate: dayjs(formDate).format('YYYY-MM-DD'),
        classTime: timeStr,
        location: formLocation.trim(),
        instructorId: formInstructorId || '',
        instructorName,
        capacity: Number(formCapacity) || 20,
        status: formStatus,
      };

      if (classroom) {
        await updateClassroom(classroom.id, data);
        notifications.show({ title: 'Success', message: 'Classroom updated.', color: 'green' });
      } else {
        await createClassroom({
          ...data,
          registeredUsers: '[]',
          waitlist: '[]',
          attendance: '[]',
          creatorId: user?.id || '',
        } as Omit<Classroom, 'id' | 'datecreated'>);
        notifications.show({ title: 'Success', message: 'Classroom created.', color: 'green' });
      }
      onSaved();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to save: ' + msg, color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const trainingOptions = trainings
    .filter((t) => t.active !== 'no')
    .map((t) => ({ value: String(t.id), label: t.name }));

  const instructorOptions = instructors.map((u) => ({
    value: String(u.id),
    label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
  }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{classroom ? 'Edit Classroom' : 'Create Class'}</Text>}
      size="lg"
    >
      <Stack gap="sm">
        <Select
          label="Training"
          placeholder="Select a training"
          data={trainingOptions}
          value={formTrainingId || null}
          onChange={handleTrainingChange}
          clearable
          searchable
          leftSection={<IconBook size={14} />}
        />
        <TextInput
          label="Title"
          placeholder="Class title"
          required
          value={formTitle}
          onChange={(e) => setFormTitle(e.target.value)}
        />
        <Group grow>
          <DateInput
            label="Date"
            placeholder="Select date"
            required
            value={formDate}
            onChange={setFormDate}
            size="sm"
          />
          <Group grow>
            <TimeInput
              label="Start Time"
              value={formStartTime}
              onChange={(e) => setFormStartTime(e.target.value)}
            />
            <TimeInput
              label="End Time"
              value={formEndTime}
              onChange={(e) => setFormEndTime(e.target.value)}
            />
          </Group>
        </Group>
        <TextInput
          label="Location"
          placeholder="Room / address or leave blank for virtual"
          value={formLocation}
          onChange={(e) => setFormLocation(e.target.value)}
          leftSection={<IconMapPin size={14} />}
        />
        <Group grow>
          <Select
            label="Instructor"
            placeholder="Select instructor"
            data={instructorOptions}
            value={formInstructorId || null}
            onChange={(v) => setFormInstructorId(v || '')}
            clearable
            searchable
            leftSection={<IconUser size={14} />}
          />
          <NumberInput
            label="Capacity"
            placeholder="Max students"
            min={1}
            value={formCapacity}
            onChange={setFormCapacity}
          />
        </Group>
        <Textarea
          label="Description"
          placeholder="Class description..."
          minRows={3}
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
        />
        <Select
          label="Status"
          data={[
            { value: 'draft', label: 'Draft' },
            { value: 'open', label: 'Open' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
          value={formStatus}
          onChange={(v) => setFormStatus(v || 'draft')}
        />
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button color="blue" onClick={handleSave} loading={saving}>
            {classroom ? 'Update Class' : 'Create Class'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Manage Class Modal ───────────────────────────────────────

interface ManageModalProps {
  opened: boolean;
  onClose: () => void;
  classroom: Classroom | null;
  trainings: Training[];
  onSaved: () => void;
}

interface ClassMaterial {
  name: string;
  type: string;
  size: number;
  data: string; // base64
  uploadedBy: string;
  uploadedAt: string;
}

const WORKER_BASE = import.meta.env.DEV
  ? (import.meta.env.VITE_WORKER_BASE || 'https://ogemc-worker.ogemc.workers.dev')
  : '/api/worker';
const KV_ORIGIN = 'https://apps.opengov.com';

function ManageModal({ opened, onClose, classroom, trainings, onSaved }: ManageModalProps) {
  const { updateClassroom, createTrainingRecord, createCertificate, certificates, loadCertificates, users } = useBlackboardStore();
  const user = useAuthStore((s) => s.user);
  const [attendanceMap, setAttendanceMap] = useState<Record<string, boolean>>({});
  const [completing, setCompleting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [downloadingCertId, setDownloadingCertId] = useState<string | null>(null);
  const [enrollUserId, setEnrollUserId] = useState<string | null>(null);
  const [enrolling, setEnrolling] = useState(false);

  // Materials
  const [materials, setMaterials] = useState<ClassMaterial[]>([]);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const materialInputRef = useRef<HTMLInputElement>(null);

  const registered = useMemo(() => parseJsonArray<RegisteredUser>(classroom?.registeredUsers), [classroom?.registeredUsers]);
  const waitlist = useMemo(() => parseJsonArray<RegisteredUser>(classroom?.waitlist), [classroom?.waitlist]);
  const training = trainings.find((t) => String(t.id) === classroom?.trainingId);

  useEffect(() => {
    if (opened && classroom) {
      const attendedIds = parseJsonArray<string>(classroom.attendance);
      const map: Record<string, boolean> = {};
      attendedIds.forEach((uid) => { map[uid] = true; });
      setAttendanceMap(map);
      loadCertificates();
      // Load materials from KV
      fetch(`${WORKER_BASE}/api/config/bb-classroom-materials-${classroom.id}`, {
        headers: { 'Content-Type': 'application/json', Origin: KV_ORIGIN },
      }).then(r => r.ok ? r.json() : []).then(d => setMaterials(Array.isArray(d) ? d : []))
        .catch(() => setMaterials([]));
    }
  }, [opened, classroom, loadCertificates]);

  const toggleAttendance = (userId: string) => {
    setAttendanceMap((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleUploadMaterial = async (file: File) => {
    if (!classroom) return;
    if (file.size > 5 * 1024 * 1024) {
      notifications.show({ title: 'Too large', message: 'Max file size is 5 MB.', color: 'red' });
      return;
    }
    setUploadingMaterial(true);
    try {
      const data = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1] || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const entry: ClassMaterial = {
        name: file.name,
        type: file.type || 'application/octet-stream',
        size: file.size,
        data,
        uploadedBy: user?.fullName || user?.email || '',
        uploadedAt: new Date().toISOString(),
      };
      const updated = [...materials, entry];
      await fetch(`${WORKER_BASE}/api/config/bb-classroom-materials-${classroom.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Origin: KV_ORIGIN },
        body: JSON.stringify(updated),
      });
      setMaterials(updated);
      notifications.show({ title: 'Uploaded', message: `${file.name} added.`, color: 'green' });
    } catch {
      notifications.show({ title: 'Error', message: 'Upload failed.', color: 'red' });
    } finally {
      setUploadingMaterial(false);
      if (materialInputRef.current) materialInputRef.current.value = '';
    }
  };

  const handleDeleteMaterial = async (index: number) => {
    if (!classroom) return;
    const updated = materials.filter((_, i) => i !== index);
    await fetch(`${WORKER_BASE}/api/config/bb-classroom-materials-${classroom.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Origin: KV_ORIGIN },
      body: JSON.stringify(updated),
    });
    setMaterials(updated);
  };

  const downloadMaterial = (mat: ClassMaterial) => {
    const blob = new Blob([Uint8Array.from(atob(mat.data), c => c.charCodeAt(0))], { type: mat.type });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = mat.name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleRemoveStudent = async (student: RegisteredUser) => {
    if (!classroom) return;
    setRemovingId(student.userId);
    try {
      const updatedRegistered = registered.filter((r) => r.userId !== student.userId);
      const updateData: Partial<Classroom> = {
        registeredUsers: JSON.stringify(updatedRegistered),
      };

      if (waitlist.length > 0) {
        const [promoted, ...remainingWaitlist] = waitlist;
        updateData.registeredUsers = JSON.stringify([...updatedRegistered, promoted]);
        updateData.waitlist = JSON.stringify(remainingWaitlist);
        notifications.show({ title: 'Removed', message: `Removed ${student.userName}. ${promoted.userName} moved from waitlist.`, color: 'blue' });
      } else {
        notifications.show({ title: 'Removed', message: `Removed ${student.userName}.`, color: 'green' });
      }

      await updateClassroom(classroom.id, updateData);
      onSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to remove student: ' + msg, color: 'red' });
    } finally {
      setRemovingId(null);
    }
  };

  const handleDownloadCert = async (student: RegisteredUser) => {
    if (!classroom) return;
    setDownloadingCertId(student.userId);
    try {
      const cert = certificates.find(
        (c) => c.userId === student.userId && String(c.trainingId) === String(classroom.trainingId || classroom.id)
      );
      if (!cert) {
        notifications.show({ title: 'Not Found', message: 'No certificate found for this student.', color: 'yellow' });
        return;
      }
      const logoBase64 = await loadLogoAsBase64();
      const pdfDoc = await generateCertificatePDF({
        studentName: cert.userName,
        trainingName: cert.trainingName,
        completionDate: cert.completedDate ? dayjs(cert.completedDate).format('MMMM D, YYYY') : '--',
        certCode: cert.certCode,
        orgName: 'Warm Springs Emergency Management Agency',
        logoBase64: logoBase64 ?? undefined,
      });
      const safeName = (cert.userName || 'student').replace(/[^a-zA-Z0-9]/g, '_');
      pdfDoc.save(`Certificate_${safeName}_${cert.certCode}.pdf`);
      notifications.show({ title: 'Downloaded', message: 'Certificate downloaded.', color: 'green' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to download certificate: ' + msg, color: 'red' });
    } finally {
      setDownloadingCertId(null);
    }
  };

  // Available users for enrollment (not already registered or waitlisted)
  const enrollableUsers = useMemo(() => {
    if (!classroom) return [];
    const regIds = new Set(registered.map(r => r.userId));
    const wlIds = new Set(waitlist.map(w => w.userId));
    return users
      .filter(u => u.active !== false && !regIds.has(String(u.id)) && !wlIds.has(String(u.id)))
      .map(u => ({ value: String(u.id), label: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email }));
  }, [classroom, users, registered, waitlist]);

  const handleEnrollStudent = async () => {
    if (!classroom || !enrollUserId) return;
    setEnrolling(true);
    const u = users.find(usr => String(usr.id) === enrollUserId);
    if (!u) { setEnrolling(false); return; }
    const entry: RegisteredUser = {
      userId: String(u.id),
      userName: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
      email: u.email,
      registeredAt: new Date().toISOString(),
    };
    try {
      const capacity = classroom.capacity || 0;
      if (registered.length >= capacity && capacity > 0) {
        await updateClassroom(classroom.id, { waitlist: JSON.stringify([...waitlist, entry]) });
        notifications.show({ title: 'Waitlisted', message: `${entry.userName} added to waitlist (class full).`, color: 'blue' });
      } else {
        await updateClassroom(classroom.id, { registeredUsers: JSON.stringify([...registered, entry]) });
        notifications.show({ title: 'Enrolled', message: `${entry.userName} enrolled.`, color: 'green' });
      }
      setEnrollUserId(null);
      onSaved();
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to enroll student.', color: 'red' });
    } finally {
      setEnrolling(false);
    }
  };

  const handleReopenClass = async () => {
    if (!classroom) return;
    setReopening(true);
    try {
      await updateClassroom(classroom.id, { status: 'open' });
      notifications.show({ title: 'Success', message: 'Class reopened.', color: 'green' });
      onSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to reopen class: ' + msg, color: 'red' });
    } finally {
      setReopening(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!classroom) return;
    setCompleting(true);

    try {
      const attendedUserIds = Object.entries(attendanceMap)
        .filter(([, v]) => v)
        .map(([uid]) => uid);

      // Update classroom status and attendance
      await updateClassroom(classroom.id, {
        status: 'completed',
        attendance: JSON.stringify(attendedUserIds),
      });

      const completedDateStr = classroom.classDate || dayjs().format('YYYY-MM-DD');
      let expiresDateStr = '';
      if (training && training.frequencyDays > 0) {
        expiresDateStr = dayjs(completedDateStr).add(training.frequencyDays, 'day').format('YYYY-MM-DD');
      }

      // Create training records + certificates for attended students
      for (const student of registered) {
        if (!attendedUserIds.includes(student.userId)) continue;

        const certCode = generateCertCode();

        await createTrainingRecord({
          userId: student.userId,
          userName: student.userName,
          trainingId: classroom.trainingId || String(classroom.id),
          trainingName: classroom.title,
          completedDate: completedDateStr,
          expiresDate: expiresDateStr,
          status: 'completed',
          notes: '',
          certificateId: certCode,
        });

        await createCertificate({
          userId: student.userId,
          userName: student.userName,
          trainingId: classroom.trainingId || String(classroom.id),
          trainingName: classroom.title,
          certCode,
          completedDate: completedDateStr,
          expiresDate: expiresDateStr,
        });
      }

      notifications.show({
        title: 'Completed',
        message: `Class marked complete. ${attendedUserIds.length} student(s) recorded.`,
        color: 'green',
      });
      onSaved();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to mark complete: ' + msg, color: 'red' });
    } finally {
      setCompleting(false);
    }
  };

  const handleGenerateCertificates = async () => {
    if (!classroom) return;
    setGenerating(true);

    try {
      await loadCertificates();
      // Use fresh state to avoid stale closure
      const freshCerts = useBlackboardStore.getState().certificates;
      const classCerts = freshCerts.filter(
        (c) => String(c.trainingId) === String(classroom.trainingId || classroom.id)
          && registered.some((r) => r.userId === c.userId)
      );

      if (classCerts.length === 0) {
        notifications.show({ title: 'Not Found', message: 'No certificates found for this class.', color: 'yellow' });
        setGenerating(false);
        return;
      }

      const logoBase64 = await loadLogoAsBase64();
      for (const cert of classCerts) {
        const pdfDoc = await generateCertificatePDF({
          studentName: cert.userName,
          trainingName: cert.trainingName,
          completionDate: cert.completedDate ? dayjs(cert.completedDate).format('MMMM D, YYYY') : '--',
          certCode: cert.certCode,
          orgName: 'Warm Springs Emergency Management Agency',
          logoBase64: logoBase64 ?? undefined,
        });
        const safeName = (cert.userName || 'student').replace(/[^a-zA-Z0-9]/g, '_');
        pdfDoc.save(`Certificate_${safeName}_${cert.certCode}.pdf`);
      }
      notifications.show({ title: 'Done', message: `Generated ${classCerts.length} certificate(s).`, color: 'green' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to generate certificates: ' + msg, color: 'red' });
    } finally {
      setGenerating(false);
    }
  };

  if (!classroom) return null;

  const classDate = classroom.classDate ? dayjs(classroom.classDate) : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>Manage Class</Text>}
      size="xl"
    >
      {/* Class details header */}
      <Paper p="sm" mb="md" bg="gray.0" withBorder>
        <Group justify="space-between" align="flex-start">
          <Box>
            <Text fw={600}>{classroom.title}</Text>
            <Group gap="lg" mt={4}>
              <Group gap={4}>
                <IconCalendar size={12} color="#627d98" />
                <Text size="xs" c="dimmed">
                  {classDate ? classDate.format('MMM D, YYYY') : '--'}
                  {classroom.classTime ? ` | ${classroom.classTime}` : ''}
                </Text>
              </Group>
              <Group gap={4}>
                <IconUser size={12} color="#627d98" />
                <Text size="xs" c="dimmed">{classroom.instructorName || '--'}</Text>
              </Group>
              <Group gap={4}>
                <IconMapPin size={12} color="#627d98" />
                <Text size="xs" c="dimmed">{classroom.location || 'Virtual'}</Text>
              </Group>
            </Group>
          </Box>
          {classStatusBadge(classroom.status)}
        </Group>
      </Paper>

      {/* Admin Enroll */}
      {classroom.status !== 'completed' && classroom.status !== 'cancelled' && (
        <Paper p="sm" mb="md" bg="blue.0" radius="sm" withBorder style={{ borderColor: 'var(--mantine-color-blue-2)' }}>
          <Group gap="sm">
            <Select
              placeholder="Select student to enroll..."
              data={enrollableUsers}
              value={enrollUserId}
              onChange={setEnrollUserId}
              searchable
              size="xs"
              style={{ flex: 1 }}
              nothingFoundMessage="All students already enrolled"
            />
            <Button size="xs" leftSection={<IconPlus size={12} />}
              onClick={handleEnrollStudent} loading={enrolling}
              disabled={!enrollUserId}>
              Enroll
            </Button>
          </Group>
        </Paper>
      )}

      {/* Registered Students */}
      <Text fw={600} size="sm" mb="xs">
        Registered Students ({registered.length}/{classroom.capacity || '--'})
      </Text>
      {registered.length === 0 ? (
        <Text c="dimmed" size="sm" ta="center" py="md">
          No students registered yet.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={500}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Registered</Table.Th>
                <Table.Th>Attendance</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {registered.map((student) => (
                <Table.Tr key={student.userId}>
                  <Table.Td>{student.userName}</Table.Td>
                  <Table.Td>{student.email || '--'}</Table.Td>
                  <Table.Td>
                    {student.registeredAt ? dayjs(student.registeredAt).format('MMM D, YYYY') : '--'}
                  </Table.Td>
                  <Table.Td>
                    <Checkbox
                      size="sm"
                      checked={!!attendanceMap[student.userId]}
                      onChange={() => toggleAttendance(student.userId)}
                      disabled={classroom.status === 'completed'}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        loading={removingId === student.userId}
                        onClick={() => handleRemoveStudent(student)}
                        disabled={classroom.status === 'completed'}
                        title="Remove student"
                      >
                        <IconX size={12} />
                      </ActionIcon>
                      {classroom.status === 'completed' && (
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="sm"
                          loading={downloadingCertId === student.userId}
                          onClick={() => handleDownloadCert(student)}
                          title="Download certificate"
                        >
                          <IconDownload size={12} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      {/* Waitlist */}
      {waitlist.length > 0 && (
        <>
          <Divider my="md" />
          <Text fw={600} size="sm" mb="xs">
            Waitlist ({waitlist.length})
          </Text>
          <Table.ScrollContainer minWidth={400}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Added</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {waitlist.map((student) => (
                  <Table.Tr key={student.userId}>
                    <Table.Td>{student.userName}</Table.Td>
                    <Table.Td>{student.email || '--'}</Table.Td>
                    <Table.Td>
                      {student.registeredAt ? dayjs(student.registeredAt).format('MMM D, YYYY') : '--'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </>
      )}

      {/* Materials */}
      <Divider my="md" />
      <Text fw={600} size="sm" mb="xs">
        Materials ({materials.length})
      </Text>
      <input ref={materialInputRef} type="file" style={{ display: 'none' }}
        accept=".pdf,.doc,.docx,.pptx,.ppt,.xlsx,.xls,.csv,.txt,.zip,.png,.jpg,.jpeg"
        onChange={e => { if (e.target.files?.[0]) handleUploadMaterial(e.target.files[0]); }} />
      <Stack gap="xs" mb="sm">
        {materials.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xs">No materials uploaded yet.</Text>
        ) : (
          materials.map((mat, i) => (
            <Group key={i} justify="space-between" p="xs" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 6 }}>
              <Group gap="sm">
                <IconFileText size={16} color="#627d98" />
                <div>
                  <Text size="sm" fw={500} style={{ cursor: 'pointer' }} c="blue" onClick={() => downloadMaterial(mat)}>
                    {mat.name}
                  </Text>
                  <Text size="xs" c="dimmed">{(mat.size / 1024).toFixed(0)} KB — {mat.uploadedBy} — {dayjs(mat.uploadedAt).format('MMM D')}</Text>
                </div>
              </Group>
              <ActionIcon size="xs" color="red" variant="subtle" onClick={() => handleDeleteMaterial(i)}>
                <IconX size={12} />
              </ActionIcon>
            </Group>
          ))
        )}
        <Button size="xs" variant="light" leftSection={<IconPlus size={12} />}
          onClick={() => materialInputRef.current?.click()} loading={uploadingMaterial}>
          Upload Material
        </Button>
      </Stack>

      {/* Actions */}
      <Divider my="md" />
      <Group justify="flex-end" gap="sm">
        {classroom.status !== 'completed' && (
          <Button
            color="green"
            leftSection={<IconCheck size={14} />}
            onClick={handleMarkComplete}
            loading={completing}
          >
            Mark Complete
          </Button>
        )}
        {classroom.status === 'completed' && (
          <>
            <Button
              variant="outline"
              color="orange"
              leftSection={<IconArrowBackUp size={14} />}
              onClick={handleReopenClass}
              loading={reopening}
            >
              Reopen Class
            </Button>
            <Button
              color="blue"
              leftSection={<IconCertificate size={14} />}
              onClick={handleGenerateCertificates}
              loading={generating}
            >
              Generate Certificates
            </Button>
          </>
        )}
        <Button variant="default" onClick={onClose}>Close</Button>
      </Group>
    </Modal>
  );
}

// ── Calendar Grid View ──────────────────────────────────────

function CalendarView({
  classrooms: classes,
  isUserRegistered,
  onManage,
}: {
  classrooms: Classroom[];
  isUserRegistered: (c: Classroom) => boolean;
  onManage: (c: Classroom) => void;
}) {
  const [viewMonth, setViewMonth] = useState(dayjs());

  const startOfMonth = viewMonth.startOf('month');
  const startDay = startOfMonth.day(); // 0=Sun
  const daysInMonth = viewMonth.daysInMonth();
  const today = dayjs();

  // Build class map: day number → classes
  const classMap = useMemo(() => {
    const map = new Map<number, Classroom[]>();
    for (const c of classes) {
      if (!c.classDate) continue;
      const d = dayjs(c.classDate);
      if (d.month() === viewMonth.month() && d.year() === viewMonth.year()) {
        const day = d.date();
        if (!map.has(day)) map.set(day, []);
        map.get(day)!.push(c);
      }
    }
    return map;
  }, [classes, viewMonth]);

  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const selectedClasses = selectedDay ? (classMap.get(selectedDay) || []) : [];

  // Build 6-row grid (42 cells)
  const cells: Array<{ day: number | null }> = [];
  for (let i = 0; i < startDay; i++) cells.push({ day: null });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d });
  while (cells.length < 42) cells.push({ day: null });

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Stack gap="md">
      {/* Month navigation */}
      <Group justify="space-between">
        <ActionIcon variant="subtle" onClick={() => { setViewMonth(v => v.subtract(1, 'month')); setSelectedDay(null); }}>
          <IconChevronLeft size={18} />
        </ActionIcon>
        <Group gap="xs">
          <Text fw={700} size="lg">{viewMonth.format('MMMM YYYY')}</Text>
          {!viewMonth.isSame(today, 'month') && (
            <Button size="compact-xs" variant="subtle" onClick={() => { setViewMonth(dayjs()); setSelectedDay(null); }}>
              Today
            </Button>
          )}
        </Group>
        <ActionIcon variant="subtle" onClick={() => { setViewMonth(v => v.add(1, 'month')); setSelectedDay(null); }}>
          <IconChevronRight size={18} />
        </ActionIcon>
      </Group>

      {/* Calendar grid */}
      <Paper withBorder radius="sm" style={{ overflow: 'hidden' }}>
        {/* Weekday headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
          {WEEKDAYS.map(d => (
            <div key={d} style={{ padding: '8px 4px', textAlign: 'center', background: 'var(--mantine-color-gray-0)' }}>
              <Text size="xs" fw={600} c="dimmed">{d}</Text>
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {cells.map((cell, i) => {
            if (!cell.day) {
              return <div key={i} style={{ minHeight: 80, background: 'var(--mantine-color-gray-0)', borderRight: '1px solid var(--mantine-color-gray-2)', borderBottom: '1px solid var(--mantine-color-gray-2)' }} />;
            }
            const dayClasses = classMap.get(cell.day) || [];
            const isToday = viewMonth.date(cell.day).isSame(today, 'day');
            const isSelected = selectedDay === cell.day;
            const isPast = viewMonth.date(cell.day).isBefore(today, 'day');

            return (
              <div
                key={i}
                onClick={() => setSelectedDay(dayClasses.length > 0 ? cell.day : null)}
                style={{
                  minHeight: 80,
                  padding: 4,
                  cursor: dayClasses.length > 0 ? 'pointer' : 'default',
                  borderRight: '1px solid var(--mantine-color-gray-2)',
                  borderBottom: '1px solid var(--mantine-color-gray-2)',
                  background: isSelected ? 'var(--mantine-color-blue-0)' : isToday ? 'var(--mantine-color-yellow-0)' : undefined,
                  opacity: isPast ? 0.5 : 1,
                }}
              >
                <Text size="xs" fw={isToday ? 700 : 400} c={isToday ? 'blue' : 'dimmed'} mb={2}>
                  {cell.day}
                </Text>
                <Stack gap={2}>
                  {dayClasses.slice(0, 3).map(c => {
                    const enrolled = isUserRegistered(c);
                    return (
                      <div key={c.id}
                        onClick={(e) => { e.stopPropagation(); setSelectedDay(cell.day); onManage(c); }}
                        style={{
                        padding: '2px 4px', borderRadius: 3, fontSize: 10, fontWeight: 500, cursor: 'pointer',
                        background: enrolled ? 'var(--mantine-color-blue-1)' : c.status === 'completed' ? 'var(--mantine-color-green-1)' : 'var(--mantine-color-gray-1)',
                        color: enrolled ? 'var(--mantine-color-blue-8)' : c.status === 'completed' ? 'var(--mantine-color-green-8)' : 'var(--mantine-color-gray-7)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {c.title}
                      </div>
                    );
                  })}
                  {dayClasses.length > 3 && (
                    <Text size="xs" c="dimmed">+{dayClasses.length - 3} more</Text>
                  )}
                </Stack>
              </div>
            );
          })}
        </div>
      </Paper>

      {/* Selected day detail */}
      {selectedDay && selectedClasses.length > 0 && (
        <Paper p="md" withBorder style={{ borderLeft: '4px solid var(--mantine-color-blue-5)' }}>
          <Text fw={600} size="sm" mb="sm">
            {viewMonth.date(selectedDay).format('dddd, MMMM D, YYYY')} — {selectedClasses.length} class{selectedClasses.length > 1 ? 'es' : ''}
          </Text>
          <Stack gap="xs">
            {selectedClasses.map(c => {
              const registered = (() => { try { return JSON.parse(c.registeredUsers || '[]').length; } catch { return 0; } })();
              const enrolled = isUserRegistered(c);
              return (
                <Group key={c.id} justify="space-between" p="xs"
                  style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 6, cursor: 'pointer' }}
                  onClick={() => onManage(c)}>
                  <div>
                    <Group gap="xs">
                      <Text size="sm" fw={500} c="blue">{c.title}</Text>
                      {enrolled && <Badge size="xs" color="blue">Enrolled</Badge>}
                      {c.status === 'completed' && <Badge size="xs" color="green">Completed</Badge>}
                    </Group>
                    <Text size="xs" c="dimmed">
                      {c.classTime || 'TBD'} — {c.instructorName || 'TBD'} — {c.location || 'Virtual'}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Badge size="xs" variant="light">{registered}/{c.capacity || '--'}</Badge>
                  </Group>
                </Group>
              );
            })}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

// ── Main Classrooms Page ─────────────────────────────────────

export default function Classrooms() {
  const user = useAuthStore((s) => s.user);
  const { canEdit: canManageClasses, canAdmin } = useModulePermission('blackboard');
  const {
    classrooms, trainings, users,
    loadClassrooms, loadTrainings, loadUsers,
    updateClassroom,
  } = useBlackboardStore();

  // Derive role from global RBAC (respects demo role switcher)
  const bbUser = useMemo(() => {
    if (!user) return null;
    return users.find((u) => (u.email || '').toLowerCase() === (user.email || '').toLowerCase()) || null;
  }, [users, user]);

  const role = canAdmin ? 'admin' : canManageClasses ? 'trainer' : 'student';
  const currentUserId = bbUser ? String(bbUser.id) : (user?.id || '');

  const instructors = useMemo(() => users, [users]);

  const [initialLoad, setInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState<string | null>(role === 'student' ? 'my-classes' : 'upcoming');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Classroom | null>(null);
  const [manageTarget, setManageTarget] = useState<Classroom | null>(null);

  const fetchData = useCallback(async () => {
    await Promise.all([loadClassrooms(), loadTrainings(), loadUsers()]);
  }, [loadClassrooms, loadTrainings, loadUsers]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);
  usePolling(fetchData);

  // Keep manageTarget fresh after data reloads
  // Match by title+date since delete-and-recreate changes the Rid
  useEffect(() => {
    if (manageTarget) {
      const fresh = classrooms.find((c) => c.id === manageTarget.id)
        || classrooms.find((c) => c.title === manageTarget.title && c.classDate === manageTarget.classDate);
      if (fresh && fresh.id !== manageTarget.id) {
        setManageTarget(fresh);
      } else if (fresh && JSON.stringify(fresh) !== JSON.stringify(manageTarget)) {
        setManageTarget(fresh);
      }
    }
  }, [classrooms, manageTarget]);

  const now = dayjs();

  const { upcoming, past, drafts } = useMemo(() => {
    const up: Classroom[] = [];
    const pa: Classroom[] = [];
    const dr: Classroom[] = [];
    classrooms.forEach((c) => {
      if (c.status === 'draft') { dr.push(c); return; }
      const d = c.classDate ? dayjs(c.classDate) : null;
      if (d && d.isBefore(now, 'day')) {
        pa.push(c);
      } else {
        up.push(c);
      }
    });
    up.sort((a, b) => {
      const da = a.classDate ? dayjs(a.classDate).valueOf() : 0;
      const db2 = b.classDate ? dayjs(b.classDate).valueOf() : 0;
      return da - db2;
    });
    pa.sort((a, b) => {
      const da = a.classDate ? dayjs(a.classDate).valueOf() : 0;
      const db2 = b.classDate ? dayjs(b.classDate).valueOf() : 0;
      return db2 - da;
    });
    return { upcoming: up, past: pa, drafts: dr };
  }, [classrooms, now]);

  // My enrolled classes (for student view)
  const myClasses = useMemo(() => {
    if (!currentUserId) return [];
    return classrooms.filter(c => {
      const reg = parseJsonArray<RegisteredUser>(c.registeredUsers);
      const wl = parseJsonArray<RegisteredUser>(c.waitlist);
      return reg.some(r => r.userId === currentUserId) || wl.some(r => r.userId === currentUserId);
    }).sort((a, b) => {
      const da = a.classDate ? dayjs(a.classDate).valueOf() : 0;
      const db = b.classDate ? dayjs(b.classDate).valueOf() : 0;
      return da - db;
    });
  }, [classrooms, currentUserId]);

  const isUserRegistered = useCallback(
    (classroom: Classroom) => {
      if (!currentUserId) return false;
      const reg = parseJsonArray<RegisteredUser>(classroom.registeredUsers);
      const wl = parseJsonArray<RegisteredUser>(classroom.waitlist);
      return reg.some((r) => r.userId === currentUserId) || wl.some((r) => r.userId === currentUserId);
    },
    [currentUserId]
  );

  const handleRegister = async (classroom: Classroom) => {
    if (!bbUser && !user) return;
    const uid = currentUserId;
    const reg = parseJsonArray<RegisteredUser>(classroom.registeredUsers);
    const wl = parseJsonArray<RegisteredUser>(classroom.waitlist);
    const capacity = classroom.capacity || 0;

    const entry: RegisteredUser = {
      userId: uid,
      userName: bbUser
        ? `${bbUser.firstName || ''} ${bbUser.lastName || ''}`.trim()
        : user?.fullName || '',
      email: bbUser?.email || user?.email || '',
      registeredAt: new Date().toISOString(),
    };

    try {
      if (reg.length >= capacity) {
        await updateClassroom(classroom.id, { waitlist: JSON.stringify([...wl, entry]) });
        notifications.show({ title: 'Waitlisted', message: 'Added to waitlist. You will be notified if a spot opens.', color: 'blue' });
      } else {
        await updateClassroom(classroom.id, { registeredUsers: JSON.stringify([...reg, entry]) });
        notifications.show({ title: 'Registered', message: 'Successfully registered for class!', color: 'green' });
      }
      await loadClassrooms();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to register: ' + msg, color: 'red' });
    }
  };

  const handleUnregister = async (classroom: Classroom) => {
    if (!currentUserId) return;
    const uid = currentUserId;
    const reg = parseJsonArray<RegisteredUser>(classroom.registeredUsers);
    const wl = parseJsonArray<RegisteredUser>(classroom.waitlist);

    const wasRegistered = reg.some((r) => r.userId === uid);
    const updatedRegistered = reg.filter((r) => r.userId !== uid);
    const updatedWaitlist = wl.filter((r) => r.userId !== uid);

    const updateData: Partial<Classroom> = {
      registeredUsers: JSON.stringify(updatedRegistered),
      waitlist: JSON.stringify(updatedWaitlist),
    };

    // Promote first waitlisted user if a registered user left
    if (wasRegistered && wl.length > 0) {
      const [promoted, ...remainingWaitlist] = wl;
      updateData.registeredUsers = JSON.stringify([...updatedRegistered, promoted]);
      updateData.waitlist = JSON.stringify(remainingWaitlist);
    }

    try {
      await updateClassroom(classroom.id, updateData);
      notifications.show({ title: 'Unregistered', message: 'Successfully unregistered from class.', color: 'green' });
      await loadClassrooms();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      notifications.show({ title: 'Error', message: 'Failed to unregister: ' + msg, color: 'red' });
    }
  };

  // ── Card-grid pagination ─────────────────────────────────
  const CARD_PAGE_SIZE = 12;
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [draftsPage, setDraftsPage] = useState(1);

  // Reset to page 1 when list length changes (e.g. after data reload)
  useEffect(() => { setUpcomingPage(1); }, [upcoming.length]);
  useEffect(() => { setPastPage(1); }, [past.length]);
  useEffect(() => { setDraftsPage(1); }, [drafts.length]);

  if (initialLoad) {
    return (
      <Center h={400}>
        <Loader color="blue" />
      </Center>
    );
  }

  const renderPaginationControls = (
    list: Classroom[],
    page: number,
    setPage: Dispatch<SetStateAction<number>>,
  ) => {
    const totalPages = Math.max(1, Math.ceil(list.length / CARD_PAGE_SIZE));
    if (totalPages <= 1) return null;
    return (
      <Group justify="center" mt="sm" gap="xs">
        <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
        <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const start = Math.max(1, Math.min(page - 2, totalPages - 4));
          const p2 = start + i;
          return p2 <= totalPages ? (
            <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
          ) : null;
        })}
        <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
        <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
        <Text size="xs" c="dimmed" ml="xs">{list.length} total</Text>
      </Group>
    );
  };

  const renderCards = (list: Classroom[], page: number, setPage: Dispatch<SetStateAction<number>>) => {
    if (list.length === 0) {
      return (
        <Paper p="xl" withBorder>
          <Center>
            <Box ta="center">
              <IconSettings size={32} color="#868e96" style={{ marginBottom: 8 }} />
              <Text c="dimmed" size="sm">No classrooms found.</Text>
            </Box>
          </Center>
        </Paper>
      );
    }
    const pageRows = list.slice((page - 1) * CARD_PAGE_SIZE, page * CARD_PAGE_SIZE);
    return (
      <>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {pageRows.map((c) => (
            <ClassCard
              key={c.id}
              classroom={c}
              currentUserId={currentUserId}
              userRole={role}
              isRegistered={isUserRegistered(c)}
              onManage={setManageTarget}
              onEdit={(cls) => { setEditTarget(cls); setCreateOpen(true); }}
              onRegister={handleRegister}
              onUnregister={handleUnregister}
            />
          ))}
        </SimpleGrid>
        {renderPaginationControls(list, page, setPage)}
      </>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Box>
          <Text size="10px" fw={700} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: 2 }}>
            Training Management
          </Text>
          <Text size="xl" fw={700} c="#1a365d">
            Classrooms
          </Text>
        </Box>
        {(role === 'admin' || role === 'trainer') && (
          <Button
            color="blue"
            leftSection={<IconPlus size={14} />}
            onClick={() => { setEditTarget(null); setCreateOpen(true); }}
          >
            Create Class
          </Button>
        )}
      </Group>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="my-classes" leftSection={<IconSchool size={14} />}>
            My Classes ({myClasses.length})
          </Tabs.Tab>
          <Tabs.Tab value="upcoming" leftSection={<IconCalendar size={14} />}>
            Upcoming ({upcoming.length})
          </Tabs.Tab>
          <Tabs.Tab value="past" leftSection={<IconHistory size={14} />}>
            Past ({past.length})
          </Tabs.Tab>
          {(role === 'admin' || role === 'trainer') && (
            <Tabs.Tab value="drafts" leftSection={<IconFileText size={14} />}>
              Drafts ({drafts.length})
            </Tabs.Tab>
          )}
          <Tabs.Tab value="calendar" leftSection={<IconCalendar size={14} />}>
            Calendar
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="my-classes">
          {myClasses.length === 0 ? (
            <Paper p="xl" withBorder>
              <Center>
                <Stack align="center" gap="sm">
                  <IconSchool size={40} color="#868e96" style={{ opacity: 0.4 }} />
                  <Text c="dimmed" size="sm">You are not enrolled in any classes.</Text>
                  <Text c="dimmed" size="xs">Browse the Upcoming tab to register for classes.</Text>
                </Stack>
              </Center>
            </Paper>
          ) : (
            <Stack gap="md">
              {myClasses.map(c => {
                const reg = parseJsonArray<RegisteredUser>(c.registeredUsers);
                const wl = parseJsonArray<RegisteredUser>(c.waitlist);
                const isWaitlisted = wl.some(w => w.userId === currentUserId) && !reg.some(r => r.userId === currentUserId);
                const classDate = c.classDate ? dayjs(c.classDate) : null;
                const isPast = classDate ? classDate.isBefore(now, 'day') : false;
                const isCompleted = c.status === 'completed';
                const training = trainings.find(t => String(t.id) === c.trainingId);

                return (
                  <Paper key={c.id} p="md" withBorder
                    style={{ borderLeft: `4px solid ${isCompleted ? '#16a34a' : isPast ? '#868e96' : '#3b82f6'}` }}>
                    <Group justify="space-between" align="flex-start" mb="sm">
                      <div>
                        <Group gap="xs" mb={4}>
                          <Text fw={600} size="md">{c.title}</Text>
                          {isWaitlisted && <Badge size="sm" color="yellow">Waitlisted</Badge>}
                          {isCompleted && <Badge size="sm" color="green">Completed</Badge>}
                          {!isCompleted && !isPast && !isWaitlisted && <Badge size="sm" color="blue">Enrolled</Badge>}
                          {isPast && !isCompleted && <Badge size="sm" color="gray">Past</Badge>}
                        </Group>
                        {training && <Text size="xs" c="dimmed">Training: {training.name}</Text>}
                      </div>
                      {!isCompleted && !isPast && (
                        <Button size="xs" variant="outline" color="red" onClick={() => handleUnregister(c)}>
                          Unregister
                        </Button>
                      )}
                    </Group>

                    <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mb="sm">
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Date</Text>
                        <Group gap={4}>
                          <IconCalendar size={12} color="#627d98" />
                          <Text size="sm" fw={500}>{classDate ? classDate.format('ddd, MMM D, YYYY') : 'TBD'}</Text>
                        </Group>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Time</Text>
                        <Text size="sm" fw={500}>{c.classTime || 'TBD'}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Instructor</Text>
                        <Text size="sm" fw={500}>{c.instructorName || 'TBD'}</Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Location</Text>
                        <Text size="sm" fw={500}>{c.location || 'Virtual'}</Text>
                      </div>
                    </SimpleGrid>

                    <Group gap="xs">
                      <Badge size="xs" variant="light" color="gray">
                        {reg.length}/{c.capacity || '--'} enrolled
                      </Badge>
                      {training?.frequencyDays ? (
                        <Badge size="xs" variant="light" color="orange">
                          Recertify every {training.frequencyDays} days
                        </Badge>
                      ) : null}
                    </Group>

                    <MyClassMaterials classroomId={c.id} />
                  </Paper>
                );
              })}
            </Stack>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="upcoming">{renderCards(upcoming, upcomingPage, setUpcomingPage)}</Tabs.Panel>
        <Tabs.Panel value="past">{renderCards(past, pastPage, setPastPage)}</Tabs.Panel>
        <Tabs.Panel value="drafts">{renderCards(drafts, draftsPage, setDraftsPage)}</Tabs.Panel>

        <Tabs.Panel value="calendar">
          <CalendarView classrooms={[...upcoming, ...past]} isUserRegistered={isUserRegistered} onManage={setManageTarget} />
        </Tabs.Panel>
      </Tabs>

      {/* Create / Edit Modal */}
      <CreateEditModal
        opened={createOpen}
        onClose={() => { setCreateOpen(false); setEditTarget(null); }}
        classroom={editTarget}
        trainings={trainings}
        instructors={instructors}
        onSaved={fetchData}
      />

      {/* Manage Modal */}
      <ManageModal
        opened={!!manageTarget}
        onClose={() => setManageTarget(null)}
        classroom={manageTarget}
        trainings={trainings}
        onSaved={fetchData}
      />
    </Box>
  );
}
